/**
 * Cookie Consent Utilities
 *
 * Constants and helpers for cookie consent management.
 * Middleware clears cookies for EU/UK users who haven't acknowledged.
 * The local cookie is the single source of truth.
 */

import type { ResponseCookie } from 'next/dist/compiled/@edge-runtime/cookies';

export enum CookieConsentChoice {
  PENDING = 'pending',
  ACKNOWLEDGED = 'true',
}

/**
 * Cookie key for storing the user's consent region.
 * Set server-side so client instrumentation can check it.
 * @deprecated Use getConsentRegion() which reads from window.__CONSENT_REGION__
 */
export const CONSENT_REGION_KEY = 'consent_region';

/**
 * Local cookie key for consent status.
 * Readable by client-side JavaScript for fast consent checks.
 */
export const COOKIE_CONSENT_KEY = 'cookie_ack';

// Declare global variable for TypeScript
declare global {
  interface Window {
    __CONSENT_REGION__?: string;
  }
}

/**
 * Standard cookie options for the consent cookie.
 * Note: domain is set dynamically via getConsentCookieOptions() to support
 * both vibes.ai and www.vibes.ai hostnames in production.
 */
export const CONSENT_COOKIE_OPTIONS: Partial<ResponseCookie> = {
  httpOnly: false, // Client JS needs to read this
  secure: true,
  sameSite: 'lax',
  maxAge: 60 * 60 * 24 * 365, // 1 year
  path: '/',
};

/**
 * Get consent cookie options with appropriate domain for the current hostname.
 * This ensures the cookie works across vibes.ai and www.vibes.ai.
 *
 * @param hostname - The current request hostname (e.g., 'vibes.ai', 'www.vibes.ai')
 * @returns Cookie options with domain set appropriately
 */
export function getConsentCookieOptions(hostname?: string): Partial<ResponseCookie> {
  const options = { ...CONSENT_COOKIE_OPTIONS };

  // Set domain for vibes.ai to share cookie between vibes.ai and www.vibes.ai
  if (hostname && (hostname === 'vibes.ai' || hostname.endsWith('.vibes.ai'))) {
    options.domain = '.vibes.ai';
  }

  return options;
}

/**
 * Parse the consent cookie value from document.cookie (client-side only).
 * Returns null if the cookie doesn't exist.
 */
function parseConsentCookie(): string | null {
  if (typeof document === 'undefined') return null;

  const cookies = document.cookie.split('; ');
  for (const cookie of cookies) {
    const [name, value] = cookie.split('=');
    if (name === COOKIE_CONSENT_KEY) {
      return value;
    }
  }
  return null;
}

/**
 * Check if analytics can be loaded (client-side).
 * Returns true if:
 * - Region doesn't require consent (ROW, BR), OR
 * - User has acknowledged consent
 */
export function canLoadAnalytics(): boolean {
  return canInitializeTracking();
}

/**
 * Check if cookies can be set (client-side).
 * Returns true only if user has acknowledged cookies.
 * Use this before setting any non-essential cookies.
 *
 * @example
 * if (canSetCookies()) {
 *   document.cookie = 'my-preference=value; path=/';
 * }
 */
export function canSetCookies(): boolean {
  const value = parseConsentCookie();
  return value === CookieConsentChoice.ACKNOWLEDGED;
}

/**
 * Check if the user has acknowledged cookies (client-side).
 * Alias for canSetCookies() for semantic clarity.
 */
export function hasConsentAck(): boolean {
  return canSetCookies();
}

/**
 * Get the current consent choice from cookies (client-side).
 *
 * @returns The current consent choice, or PENDING if no cookie exists
 */
export function getConsentFromCookie(): CookieConsentChoice {
  const value = parseConsentCookie();

  if (!value) return CookieConsentChoice.PENDING;

  return Object.values(CookieConsentChoice).includes(value as CookieConsentChoice)
    ? (value as CookieConsentChoice)
    : CookieConsentChoice.PENDING;
}

/**
 * Get the consent region (client-side).
 * Reads from window.__CONSENT_REGION__ set by server-rendered script.
 * Falls back to data attribute on HTML element.
 */
export function getConsentRegionFromCookie(): string | null {
  if (typeof window === 'undefined') return null;

  // Primary: read from global variable (set by inline script in head)
  if (window.__CONSENT_REGION__) {
    return window.__CONSENT_REGION__;
  }

  // Fallback: read from data attribute on HTML element
  const htmlElement = document.documentElement;
  const region = htmlElement.getAttribute('data-consent-region');
  if (region) {
    return region;
  }

  return null;
}

/**
 * Check if the current region requires explicit consent before loading analytics.
 * All regions now require consent.
 */
export function regionRequiresConsent(_region: string | null): boolean {
  return true;
}

/**
 * Check if analytics/tracking can be initialized (client-side).
 * All regions require consent — returns true only after acknowledgment.
 */
export function canInitializeTracking(): boolean {
  const consent = getConsentFromCookie();
  return consent === CookieConsentChoice.ACKNOWLEDGED;
}
